import React from "react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CtaBand } from "@/pages/Home";
import { COMPANY } from "@/data/site";

const TEAM_IMG =
  "https://images.pexels.com/photos/8369249/pexels-photo-8369249.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUES = [
  { title: "Counterparty, not conduit", body: "We take a principal view of your flow. That alignment shapes every pricing, execution and risk decision we make." },
  { title: "Wholesale by design", body: "ET Prime serves professional and eligible counterparties only — with no compromise on standards." },
  { title: "Transparency as standard", body: "Once licensed, real-time analytics and reporting will be open to every client. In this market, visibility is not a feature — it is the standard we are building to." },
  { title: "Australian governance", body: "Structured and operated in Australia as a wholesale-only business. Our Australian financial services licence (AFSL 700116), which includes market making, is currently in draft and has not been granted." },
];

const FACTS = [
  { k: "Legal entity", v: COMPANY.legalName },
  { k: "ACN", v: COMPANY.acn },
  { k: "ABN", v: COMPANY.abn },
  { k: "Registered", v: `${COMPANY.registered}, ${COMPANY.jurisdiction}` },
  { k: "Registered office", v: COMPANY.addressLines.join(", ") },
  { k: "Regulatory", v: COMPANY.afslStatus },
  { k: "Client scope", v: COMPANY.clientNotice },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About ET Prime"
        title="An Australian prime-of-prime, built for institutions."
        subtitle="ET Prime Pty Ltd was founded to give brokers, funds and professional desks a serious wholesale counterparty — one that pairs capable execution technology with genuine, relationship-led accountability."
      />

      {/* NARRATIVE + IMAGE */}
      <section className="py-24 md:py-32 relative z-10" data-testid="about-narrative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <span className="overline">Our position</span>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white mt-5 leading-tight">
              The intermediary the next market cycle demands.
            </h2>
            <p className="text-[#A1A5AB] mt-6 leading-relaxed">
              As the major banks tighten direct prime access, mid-size brokers and funds need a partner who can
              aggregate institutional liquidity and stand behind execution as a
              principal. That is the gap ET Prime was built to fill.
            </p>
            <p className="text-[#A1A5AB] mt-4 leading-relaxed">
              We combine institutional-grade execution technology and real-time risk analytics with a high-touch dealing relationship — and a
              a wholesale AFSL — currently in draft and not yet granted — whose market-making authorisation is intended to let us price flow, not merely route it.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative spotlight-frame">
              <div className="absolute -inset-3 border border-[#C8A45A]/20 z-10 pointer-events-none" />
              <img src={TEAM_IMG} alt="ET Prime dealing desk" className="relative w-full h-[420px] object-cover" data-testid="about-image" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 relative z-10 border-y border-white/10" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="overline">What we stand for</span>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white mt-5">Principles that price into everything.</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 mt-16">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06} className="bg-[#0A0D12] p-10 hover:bg-[#11151C] transition-colors">
                <h3 className="font-display text-xl text-white">{v.title}</h3>
                <p className="text-[#A1A5AB] mt-3 leading-relaxed">{v.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORPORATE FACTS */}
      <section className="py-24 md:py-32 relative z-10 border-t border-white/10" data-testid="about-facts">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="overline">Corporate details</span>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white mt-5">Governance & registration.</h2>
          </Reveal>
          <div className="mt-12 border border-white/10 divide-y divide-white/10">
            {FACTS.map((f, i) => (
              <Reveal key={f.k} delay={i * 0.05}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-6" data-testid={`fact-${i}`}>
                  <div className="text-xs uppercase tracking-widest text-[#737882]">{f.k}</div>
                  <div className="sm:col-span-2 text-[#A1A5AB]">{f.v}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
